import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { Badge, Card, CardContent } from "@fleet/ui";
import { Check, Moon, Sun } from "@fleet/icons";
import type { ThemesRecord } from "@custodia/powersync";

interface ThemeGalleryCardProps {
  theme: ThemesRecord;
  isSelected: boolean;
  onSelect: (themeId: string) => void;
  t: (s: MessageDescriptor) => string;
}

export function ThemeGalleryCard(props: ThemeGalleryCardProps) {
  const colors = () => glowColors(props.theme);

  const handleSelect = () => {
    props.onSelect(props.theme.id);
  };

  return (
    <Card
      data-testid="theme-card"
      data-selected={props.isSelected}
      class={`group overflow-hidden transition-all cursor-pointer ${
        props.isSelected
          ? "ring-2 ring-primary shadow-lg shadow-primary/20"
          : "hover:ring-2 hover:ring-primary/50"
      }`}
      onClick={handleSelect}
    >
      <CardContent class="p-0">
        {/* Preview area */}
        <div
          class="h-40 w-full relative flex items-center justify-center overflow-hidden"
          style={{ "background-color": props.theme.background ?? "#000" }}
        >
          {/* Glow orbs */}
          <Show when={colors()[0]}>
            {(c) => (
              <div
                class="absolute -top-5 -right-5 h-24 w-24 rounded-full blur-2xl"
                style={{ "background-color": c() }}
              />
            )}
          </Show>
          <Show when={colors()[1]}>
            {(c) => (
              <div
                class="absolute -bottom-5 -left-5 h-20 w-20 rounded-full blur-2xl"
                style={{ "background-color": c() }}
              />
            )}
          </Show>

          {/* Mini app preview */}
          <div
            class="w-4/5 h-2/3 rounded-xl border border-white/10 shadow-2xl p-3 flex flex-col gap-2 overflow-hidden relative z-10"
            style={{
              "background-color": props.theme.surface ?? "",
              color: props.theme.foreground ?? "",
            }}
          >
            <div class="flex items-center justify-between">
              <div class="h-2 w-1/3 rounded-full bg-current opacity-20" />
              <div
                class="h-4 w-4 rounded-full"
                style={{ "background-color": props.theme.accent ?? "" }}
              />
            </div>
            <div class="space-y-1.5">
              <div class="h-1.5 w-full rounded-full bg-current opacity-10" />
              <div class="h-1.5 w-5/6 rounded-full bg-current opacity-10" />
              <div class="h-1.5 w-4/6 rounded-full bg-current opacity-10" />
            </div>
          </div>

          {/* Dark/light badge */}
          <Badge
            variant="secondary"
            class="absolute top-3 right-3 bg-white/10 backdrop-blur-md border-white/20 text-white flex gap-1 items-center"
          >
            {props.theme.is_dark ? <Moon class="h-3 w-3" /> : <Sun class="h-3 w-3" />}
            <span class="text-[10px] uppercase font-bold tracking-wider">
              {props.theme.is_dark ? props.t(msg`Dark`) : props.t(msg`Light`)}
            </span>
          </Badge>
        </div>

        {/* Theme info */}
        <div class="p-3 flex items-center justify-between bg-card">
          <div class="min-w-0 flex-1">
            <p class="font-bold text-sm truncate">{props.theme.name}</p>
            <div class="flex gap-1 mt-1">
              <Show when={props.theme.accent}>
                {(a) => <div class="h-2 w-2 rounded-full" style={{ "background-color": a() }} />}
              </Show>
              <Show when={props.theme.background}>
                {(b) => (
                  <div
                    class="h-2 w-2 rounded-full border border-border"
                    style={{ "background-color": b() }}
                  />
                )}
              </Show>
              <Show when={props.theme.surface}>
                {(s) => <div class="h-2 w-2 rounded-full" style={{ "background-color": s() }} />}
              </Show>
            </div>
          </div>
          <Show when={props.isSelected}>
            <div class="ml-2 h-6 w-6 shrink-0 rounded-full bg-primary flex items-center justify-center text-primary-foreground shadow-lg animate-in zoom-in-50 duration-200">
              <Check class="h-3.5 w-3.5" />
            </div>
          </Show>
        </div>
      </CardContent>
    </Card>
  );
}

function glowColors(theme: ThemesRecord): string[] {
  if (!theme.glow_colors) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(theme.glow_colors);
    if (Array.isArray(parsed)) {
      return parsed.filter((x): x is string => typeof x === "string");
    }
  } catch {
    // ignore
  }
  return [];
}
